import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { isNilOrEmpty, isNotNilOrEmpty } from 'ramda-adjunct';

import Error from '../Error';

const propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  helperText: PropTypes.string,
};
const defaultProps = {
  rows: 4,
  placeholder: '',
  required: false,
  error: null,
  value: undefined,
  onChange: null,
  onBlur: null,
  onFocus: null,
  helperText: null,
};

const TextArea = forwardRef(
  (
    {
      label,
      placeholder,
      required,
      rows,
      error,
      name,
      value,
      onChange,
      onBlur,
      onFocus,
      helperText,
      ...rest
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);

    useEffect(() => {
      if (isNotNilOrEmpty(value)) setHasValue(true);
    }, [value]);

    return (
      <div className="flex flex-col">
        <div
          className={cx(
            'shadow-faq rounded-md relative transition-opacity ease-in-out duration-150',
            {
              'opacity-50 cursor-not-allowed': rest.disabled,
              'bg-white': !hasValue || isFocused,
              'bg-cardif-gray-light': hasValue && !isFocused,
            }
          )}
        >
          <label
            htmlFor={name}
            className={cx(
              'absolute pointer-events-none transition-all ease-out duration-200',
              {
                'text-gray-900': !error && !isFocused && !hasValue,
                'text-red-600': !!error && !isFocused,
                'text-xs left-3 top-1.5': isFocused || hasValue,
                'text-cardif-green-light':
                  isFocused || (hasValue && !error && !rest.disabled),
                'left-4 top-7': !isFocused && !hasValue,
              }
            )}
          >
            {label}
            {/* {required ? '*' : ''} */}
          </label>
          <textarea
            ref={ref}
            {...rest}
            id={name}
            name={name}
            rows={rows}
            value={value}
            onFocus={(e) => {
              setIsFocused(true);
              if (onFocus) onFocus(e);
            }}
            onBlur={(e) => {
              if (onBlur) onBlur(e);
              setIsFocused(false);
              if (isNilOrEmpty(e.target.value)) setHasValue(false);
            }}
            onInput={(e) => {
              if (onChange) onChange(e);
              if (isNotNilOrEmpty(e.target.value)) setHasValue(true);
              if (isNilOrEmpty(e.target.value)) setHasValue(false);
            }}
            className={cx(
              'px-3 py-2 pt-8 w-full outline-none bg-transparent resize-none focus:ring-0 rounded-sm border-l-0 border-t-0 border-r-0 border-b-4',
              {
                'placeholder:invisible': !isFocused,
                'border-red-600': !!error && !isFocused,
                'focus:border-cardif-green-light':
                  !error || (error && isFocused),
                'border-cardif-green-light': hasValue && !error,
              }
            )}
            placeholder={placeholder}
            required={required}
          />
        </div>
        <div className="min-h-[36px] pr-14 mt-2 relative text-xs text-gray-600">
          <Error error={error} />
          {!!helperText && <span className="block pl-4">{helperText}</span>}
          <span className="absolute top-0 right-2">
            {value ? value.length : 0}/500
          </span>
        </div>
      </div>
    );
  }
);

TextArea.propTypes = propTypes;
TextArea.defaultProps = defaultProps;
TextArea.displayName = 'TextArea';

export default TextArea;
